import { useState, useRef, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Server,
  Layers,
  Settings,
  Activity,
  FileText,
  FolderTree,
  HardDrive,
  Scan,
  AlertTriangle,
  LogOut,
  ChevronDown,
  ChevronRight,
  Shield,
  UserCircle,
  GitBranch,
  Building2,
  ClipboardCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { myApprovalsApi } from "@/api/client";

// Secondary navigation items (after Dashboard, Release Cycles, and Approvals)
const secondaryNavigation = [
  { name: "Scans", href: "/scans", icon: Scan },
  { name: "Drift Events", href: "/drift-events", icon: AlertTriangle },
  { name: "Reports", href: "/reports", icon: FileText },
];

// Infrastructure group items
const infrastructureNavigation = [
  { name: "Groups", href: "/groups", icon: FolderTree },
  { name: "Environments", href: "/environments", icon: Server },
  { name: "Services", href: "/services", icon: Layers },
  { name: "Servers", href: "/servers", icon: HardDrive },
];

// Commented out for now - can be added back later
// const additionalNavigation = [
//   { name: "Releases", href: "/releases", icon: FileCode },
//   { name: "Deployment Requests", href: "/deployment-requests", icon: CheckCircle },
//   { name: "Deployments", href: "/deployments", icon: Rocket },
// ];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isInfrastructureOpen, setIsInfrastructureOpen] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch pending approvals count for approvers
  const { data: approvalsData } = useQuery({
    queryKey: ["my-approvals"],
    queryFn: myApprovalsApi.listPending,
    enabled: !!user?.is_approver,
    refetchInterval: 15000, // Refresh every 15 seconds for faster badge updates
    refetchOnWindowFocus: true,
  });
  const pendingApprovalsCount = approvalsData?.count ?? 0;

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getUserInitials = () => {
    if (!user?.name) return "U";
    const parts = user.name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  const getPrimaryRole = () => {
    const primaryRole = user?.roles?.find((r) => r.is_primary);
    return primaryRole?.role_name || user?.roles?.[0]?.role_name || null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-gray-900">
        {/* Logo */}
        <div className="flex h-16 items-center gap-2 px-6 border-b border-gray-800">
          <Activity className="h-8 w-8 text-brand-500" />
          <span className="text-xl font-semibold text-white">DeployOps</span>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 flex flex-col h-[calc(100%-4rem-8rem)]">
          <ul className="space-y-1 flex-1">
            {/* Dashboard */}
            <li>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-gray-800 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )
                }
              >
                <LayoutDashboard className="h-5 w-5" />
                Dashboard
              </NavLink>
            </li>

            {/* Release Cycles */}
            <li>
              <NavLink
                to="/release-cycles"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-gray-800 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )
                }
              >
                <GitBranch className="h-5 w-5" />
                Release Cycles
              </NavLink>
            </li>

            {/* Approvals - only visible to approvers, placed right after Release Cycles */}
            {user?.is_approver && (
              <li>
                <NavLink
                  to="/approvals"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-gray-800 text-white"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white",
                    )
                  }
                >
                  <ClipboardCheck className="h-5 w-5" />
                  <span className="flex-1">Approvals</span>
                  {pendingApprovalsCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {pendingApprovalsCount}
                    </span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Secondary navigation items (Scans, Drift Events, Reports) */}
            {secondaryNavigation.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-gray-800 text-white"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white",
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              </li>
            ))}

            {/* Infrastructure collapsible group */}
            <li className="pt-2">
              <button
                onClick={() => setIsInfrastructureOpen(!isInfrastructureOpen)}
                className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Building2 className="h-5 w-5" />
                <span className="flex-1 text-left">Infrastructure</span>
                {isInfrastructureOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {isInfrastructureOpen && (
                <ul className="mt-1 ml-4 space-y-1">
                  {infrastructureNavigation.map((item) => (
                    <li key={item.name}>
                      <NavLink
                        to={item.href}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                            isActive
                              ? "bg-gray-800 text-white"
                              : "text-gray-400 hover:bg-gray-800 hover:text-white",
                          )
                        }
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>

          {/* Org Settings - Admin only */}
          {user?.is_org_admin && (
            <div className="pt-4 border-t border-gray-800 mt-4">
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-gray-800 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )
                }
              >
                <Settings className="h-5 w-5" />
                Org Settings
              </NavLink>
            </div>
          )}
        </nav>

        {/* User Menu */}
        <div
          ref={menuRef}
          className="absolute bottom-0 left-0 right-0 border-t border-gray-800"
        >
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-800 transition-colors"
            >
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-brand-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {getUserInitials()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || "User"}
                </p>
                <div className="flex items-center gap-1">
                  {getPrimaryRole() && (
                    <span className="text-xs text-gray-400 truncate">
                      {getPrimaryRole()}
                    </span>
                  )}
                  {user?.is_org_admin && (
                    <Shield className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                  )}
                </div>
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-gray-400 transition-transform",
                  isUserMenuOpen && "rotate-180",
                )}
              />
            </button>

            {/* Dropdown Menu */}
            {isUserMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-gray-800 rounded-md shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700">
                  <p className="text-sm text-gray-300 truncate">
                    {user?.email}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {user?.is_org_admin && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-900 text-yellow-200">
                        Admin
                      </span>
                    )}
                    {user?.is_approver && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-900 text-green-200">
                        Approver
                      </span>
                    )}
                  </div>
                </div>
                <div className="py-1">
                  <Link
                    to="/profile"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    <UserCircle className="h-4 w-4" />
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
